<template>
  <div class="file-loader-table">
    <data-table
      :headers="headers"
      :items="files"
      :loading="loading"
      :items-per-page="itemsPerPage"
      :hide-default-footer="files.length <= itemsPerPage"
      class="elevation-0"
      :aria-label="'File load log table'"
      :data-testid="'file-loader-table'"
    >
      <!-- Loading slot -->
      <template #progress>
        <v-progress-linear
          indeterminate
          color="primary"
          aria-live="polite"
          aria-label="Loading file data"
        />
      </template>

      <!-- Loaded Date column -->
      <template #item.loadedDate="{ item }">
        <span class="text-body-2">
          {{ formatDate(item.loadedDate) }}
        </span>
      </template>

      <!-- File Name column -->
      <template #item.fileName="{ item }">
        <div class="d-flex align-center">
          <v-icon
            v-if="item.loadStatus === 'failed'"
            small
            color="error"
            class="mr-2"
            :aria-label="'Failed file'"
          >
            mdi-close-circle
          </v-icon>
          <span
            class="text-body-2"
            :class="{ 'error--text': item.loadStatus === 'failed' }"
          >
            {{ item.fileName }}
          </span>
        </div>
      </template>

      <!-- File Type column -->
      <template #item.fileType="{ item }">
        <file-loader-type-badge :file-type="item.fileType" />
      </template>

      <!-- Load Status column -->
      <template #item.loadStatus="{ item }">
        <status-badge
          :status="item.loadStatus === 'success' ? 'Success' : 'Failed'"
          :type="item.loadStatus === 'success' ? 'success' : 'error'"
        />
      </template>

      <!-- Rows Accepted column -->
      <template #item.rowsAccepted="{ item }">
        <span
          class="text-body-2 font-weight-medium"
          :class="item.rowsAccepted > 0 ? 'success--text' : 'grey--text'"
        >
          {{ item.rowsAccepted.toLocaleString() }}
        </span>
      </template>

      <!-- Rows Rejected column -->
      <template #item.rowsRejected="{ item }">
        <span
          class="text-body-2 font-weight-medium"
          :class="item.rowsRejected > 0 ? 'error--text' : 'grey--text'"
        >
          {{ item.rowsRejected.toLocaleString() }}
        </span>
      </template>

      <!-- Submissions Created column -->
      <template #item.submissionsCreated="{ item }">
        <span
          class="text-body-2 font-weight-medium"
          :class="item.submissionsCreated > 0 ? 'primary--text' : 'grey--text'"
          :style="item.submissionsCreated > 0 ? 'cursor: pointer; text-decoration: underline;' : ''"
          @click="item.submissionsCreated > 0 ? handleSubmissionsClick(item) : null"
        >
          {{ item.submissionsCreated.toLocaleString() }}
        </span>
      </template>

      <!-- Duration column -->
      <template #item.durationSeconds="{ item }">
        <span class="text-body-2 grey--text text--darken-1">
          {{ formatDuration(item.durationSeconds) }}
        </span>
      </template>

      <!-- Empty state -->
      <template #no-data>
        <div class="text-center py-8">
          <v-icon size="48" color="grey lighten-1">mdi-file-document-outline</v-icon>
          <div class="text-h6 grey--text mt-4">No files found</div>
          <div class="text-body-2 grey--text">Files will appear here once they are processed</div>
        </div>
      </template>
    </data-table>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import type { PropType } from 'vue'
import DataTable from '../DataTable/DataTable.vue'
import StatusBadge from '../StatusBadge/StatusBadge.vue'
import FileLoaderTypeBadge from './FileLoaderTypeBadge.vue'
import type { FileLoadJob } from './types'
import { formatDate, formatDuration } from '../../utils/formatters'

export default Vue.extend({
  name: 'FileLoaderTable',

  components: {
    DataTable,
    StatusBadge,
    FileLoaderTypeBadge
  },

  props: {
    /** Array of file load jobs */
    files: {
      type: Array as PropType<FileLoadJob[]>,
      required: true
    },
    /** Loading state */
    loading: {
      type: Boolean,
      default: false
    },
    /** Items per page */
    itemsPerPage: {
      type: Number,
      default: 10
    }
  },

  data() {
    return {
      headers: [
        {
          text: 'Loaded Date',
          value: 'loadedDate',
          sortable: true,
          width: '180px'
        },
        {
          text: 'File Name',
          value: 'fileName',
          sortable: true
        },
        {
          text: 'File Type',
          value: 'fileType',
          sortable: true,
          width: '180px'
        },
        {
          text: 'Status',
          value: 'loadStatus',
          sortable: true,
          width: '100px'
        },
        {
          text: 'Rows Accepted',
          value: 'rowsAccepted',
          sortable: true,
          align: 'end' as const,
          width: '140px'
        },
        {
          text: 'Rows Rejected',
          value: 'rowsRejected',
          sortable: true,
          align: 'end' as const,
          width: '140px'
        },
        {
          text: 'Submissions Created',
          value: 'submissionsCreated',
          sortable: true,
          align: 'end' as const,
          width: '160px'
        },
        {
          text: 'Duration',
          value: 'durationSeconds',
          sortable: true,
          align: 'end' as const,
          width: '100px'
        }
      ]
    }
  },

  methods: {
    formatDate,
    formatDuration,

    handleSubmissionsClick(item: FileLoadJob) {
      this.$emit('view-submissions', item)
    }
  }
})
</script>

<style scoped>
.file-loader-table {
  background-color: white;
  border-radius: var(--radius);
  overflow: hidden;
}

.file-loader-table >>> .v-data-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: var(--radius);
}

/* Header styling */
.file-loader-table >>> .v-data-table thead th {
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center !important;
  padding: 16px 16px !important;
  height: 56px !important;
  vertical-align: middle !important;
  background-color: white !important;
}

/* Header text alignment */
.file-loader-table >>> .v-data-table thead th span {
  display: block;
  text-align: center !important;
}

/* Row hover */
.file-loader-table >>> .v-data-table tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02) !important;
}

/* Cell styling */
.file-loader-table >>> .v-data-table tbody td {
  padding: 16px 16px !important;
  height: auto !important;
  min-height: 72px;
  vertical-align: middle !important;
}

.file-loader-table >>> .v-data-table tbody tr {
  min-height: 72px;
}
</style>
